import { useState } from 'react'
import { PLAN_WEIGHT_PCT_OPTIONS } from '../utils/positionPlanOverlay'

/**
 * The on-chart % editor for a position plan entry — a tiny preset menu that
 * opens where the entry's label pill was clicked (ChartManager relays the
 * click as `plan:weightEdit`; EdgeChart anchors this at the line's pixel y).
 * Canvas text figures cannot host a real dropdown, so the menu lives in the
 * React chrome layer, like the overlay quick-edit bar.
 */
export function PlanWeightMenu({
  top,
  left,
  current,
  maxPct = 100,
  onSelect,
  onRemove,
  onClose,
}: {
  top: number
  left: number
  /** The entry's current allocation %, for the active highlight. */
  current: number
  /** The cascade's remaining budget — options above it are disabled so no
   *  selection can push the total past 100. Every entry (including the last)
   *  picks freely below it; the total may land under 100 (partial plan). */
  maxPct?: number
  onSelect: (pct: number) => void
  /** Remove this entry leg. Absent when the plan has only one entry. */
  onRemove?: () => void
  onClose: () => void
}) {
  const [custom, setCustom] = useState('')
  const commitCustom = () => {
    const v = Math.round(Number(custom))
    if (!Number.isFinite(v) || v <= 0) return
    // The cascade clamps to the budget server-side of this menu too; the
    // min() here just keeps the UI honest.
    onSelect(Math.min(v, maxPct))
  }
  return (
    <>
      {/* Click-away backdrop — swallows exactly one click, standard menu UX. */}
      <div
        style={{ position: 'absolute', inset: 0, zIndex: 30 }}
        onClick={onClose}
        data-testid="plan-weight-backdrop"
      />
      <div
        style={{
          position: 'absolute',
          top,
          left,
          zIndex: 31,
          display: 'flex',
          flexDirection: 'column',
          padding: 3,
          borderRadius: 6,
          background: '#1e222d',
          border: '1px solid #2a2e39',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}
        role="menu"
        data-testid="plan-weight-menu"
      >
        {PLAN_WEIGHT_PCT_OPTIONS.map((pct) => {
          const disabled = pct > maxPct
          return (
            <button
              key={pct}
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={disabled ? undefined : () => onSelect(pct)}
              style={{
                padding: '3px 14px',
                borderRadius: 4,
                fontSize: 11,
                textAlign: 'left',
                color: disabled ? '#4b5058' : pct === current ? '#ffffff' : '#d1d4dc',
                background: pct === current ? '#2962ff' : 'transparent',
                border: 'none',
                cursor: disabled ? 'default' : 'pointer',
              }}
            >
              {pct}%
            </button>
          )
        })}
        {/* Free-typed % — presets AND typing, per Chris. Enter commits. */}
        <div style={{ borderTop: '1px solid #2a2e39', margin: '3px 0', paddingTop: 3 }}>
          <input
            aria-label="Custom percent"
            placeholder={`Custom (max ${maxPct}%)`}
            inputMode="numeric"
            value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitCustom()
            }}
            style={{
              width: 110,
              margin: '0 3px',
              padding: '3px 6px',
              borderRadius: 4,
              fontSize: 11,
              color: '#d1d4dc',
              background: '#131722',
              border: '1px solid #2a2e39',
              outline: 'none',
            }}
          />
        </div>
        {onRemove && (
          <button
            type="button"
            role="menuitem"
            onClick={onRemove}
            style={{
              marginTop: 3,
              borderTop: '1px solid #2a2e39',
              padding: '5px 14px 3px',
              borderRadius: 0,
              fontSize: 11,
              textAlign: 'left',
              color: '#f23645',
              background: 'transparent',
              border: 'none',
              borderTopStyle: 'solid',
              cursor: 'pointer',
            }}
          >
            Remove entry
          </button>
        )}
      </div>
    </>
  )
}
